import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import Select from 'react-select'
import getSupportedCountries, { getAllCountries } from '../../configs/supportedCountries'

const CountrySelect = ({ countryCode, handleChange, portalSupportedCountries, portalInternationalShipping }) => {
  const countries = useMemo(() => {
    // Only US unless international shipping is enabled
    const isInternationalShippingEnabled = portalInternationalShipping === true
    if (!isInternationalShippingEnabled) {
      return getSupportedCountries(['US'])
    }

    // If international shipping is enabled but no supported countries are specified,
    // allow all countries supported by the system.
    const hasPortalSupportedCountries = portalSupportedCountries.length > 0

    return hasPortalSupportedCountries ? getSupportedCountries(portalSupportedCountries) : getAllCountries()
  }, [portalInternationalShipping, portalSupportedCountries])

  const selectedCountry = useMemo(() => {
    const country = countries.find(country => country.country_code === countryCode)
    return country || {}
  }, [countryCode, countries])

  const lastAutoSelectedRef = useRef(null)

  useEffect(() => {
    if (typeof handleChange !== 'function') {
      return
    }
    const hasValidSelectedCountry = countries.some(country => country.country_code === countryCode)
    if (hasValidSelectedCountry) {
      if (lastAutoSelectedRef.current !== null) {
        lastAutoSelectedRef.current = null
      }
      return
    }
    const candidate = countries.length === 1 ? countries[0] : countries.find(country => country.country_code === 'US')
    if (!candidate) {
      return
    }
    if (countries.length > 1 && candidate.country_code !== 'US') {
      return
    }
    const cacheKey = `${candidate.country_code}:${countries.length}`
    if (lastAutoSelectedRef.current === cacheKey) {
      return
    }
    lastAutoSelectedRef.current = cacheKey
    handleChange(candidate)
  }, [countryCode, handleChange, countries])

  const customStyles = {
    singleValue: provided => {
      const overflow = 'none'
      return { ...provided, overflow }
    }
  }

  const onChange = country => {
    if (typeof handleChange === 'function') {
      handleChange(country)
    }
  }

  return (
    <Select
      value={selectedCountry}
      onChange={onChange}
      options={countries}
      getOptionLabel={option => option.name}
      getOptionValue={option => option.country_code}
      styles={customStyles}
    />
  )
}

CountrySelect.propTypes = {
  countryCode: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  portalSupportedCountries: PropTypes.arrayOf(PropTypes.string).isRequired,
  portalInternationalShipping: PropTypes.bool.isRequired
}

const mapStateToProps = state => {
  return {
    portalSupportedCountries: state.portal.supported_countries,
    portalInternationalShipping: state.portal.international_shipping
  }
}

export default connect(mapStateToProps)(CountrySelect)
